import type { AppState } from "../State";
import { loadTerrain, type TerrainAsset } from "../assets/TerrainAsset";
import type { PaletteUniforms } from "../rendering/PaletteUniforms";
import { loadDEM, manifestFromQuery } from "./dem-loader";
import type { TerrainHeightmap } from "./procedural-terrain";
import { applyTerrainEnvironment } from "./terrain-environment";
import {
  DEFAULT_TERRAIN_PRESET,
  loadTerrainPreset,
  loadTerrainPresetList,
  type TerrainPreset,
} from "./terrain-preset-loader";

export interface TerrainClassificationRaster {
  data: Uint8Array;
  width: number;
  height: number;
}

export interface TerrainRoadCoverageRaster {
  data: Uint8Array;
  width: number;
  height: number;
}

export interface TerrainPolygonFadeRaster {
  data: Uint8Array;
  width: number;
  height: number;
}

export interface RuntimeTerrain {
  presets: TerrainPreset[];
  activePreset: TerrainPreset;
  heightmap?: TerrainHeightmap;
  classification?: TerrainClassificationRaster;
  roadCoverage?: TerrainRoadCoverageRaster;
  polygonFade?: TerrainPolygonFadeRaster;
  asset?: TerrainAsset;
  usedDemOverride: boolean;
}

export async function loadRuntimeTerrain(args: {
  search: string;
  requestedTerrainId: string;
  state: AppState;
  palette: PaletteUniforms;
  baseUrl?: string;
}): Promise<RuntimeTerrain> {
  const baseUrl = args.baseUrl ?? "terrain/";
  const presets = await loadTerrainPresetList(baseUrl);
  const activePreset =
    presets.find((p) => p.id === args.requestedTerrainId) ??
    presets[0] ??
    DEFAULT_TERRAIN_PRESET;
  args.state.terrainId = activePreset.id;

  const demManifest = manifestFromQuery(args.search);
  let usedDemOverride = false;
  let heightmap = demManifest
    ? await loadDEM(demManifest).catch((err) => {
        console.error("DEM load failed; falling back to procedural:", err);
        return undefined;
      })
    : undefined;
  usedDemOverride = !!demManifest && !!heightmap;
  let classification: TerrainClassificationRaster | undefined;
  let roadCoverage: TerrainRoadCoverageRaster | undefined;
  let polygonFade: TerrainPolygonFadeRaster | undefined;

  if (!heightmap) {
    try {
      const preset = await loadTerrainPreset(activePreset);
      heightmap = preset.heightmap;
      classification = preset.classification;
      roadCoverage = preset.roadCoverage;
      polygonFade = preset.polygonFade;
      applyTerrainEnvironment(preset.meta, args.state, args.palette);
      console.log(
        `[Terrain] preset "${preset.preset.id}" loaded: ` +
          `${preset.meta.width_m.toFixed(0)}×${preset.meta.height_m.toFixed(0)} m, ` +
          `extent=${preset.heightmap.extent.toFixed(0)} m, ` +
          `WL=${preset.meta.water_level_m.toFixed(1)} m npm` +
          (classification
            ? `, classification=${classification.width}×${classification.height}`
            : ""),
      );
    } catch (err) {
      console.warn(
        "[Terrain] preset load failed; falling back to procedural:",
        err,
      );
    }
  }

  const asset = await loadActiveTerrainAsset(activePreset, baseUrl, heightmap);
  return {
    presets,
    activePreset,
    heightmap,
    classification,
    roadCoverage,
    polygonFade,
    asset,
    usedDemOverride,
  };
}

async function loadActiveTerrainAsset(
  preset: TerrainPreset,
  baseUrl: string,
  heightmap?: TerrainHeightmap,
): Promise<TerrainAsset | undefined> {
  try {
    const terrain = await loadTerrain(
      normalizeTerrainUrl(preset.url, baseUrl),
      preset.id,
      preset.name,
    );
    if (heightmap) {
      terrain.renderHeights = heightmap.data;
      terrain.renderWidth = heightmap.resolution;
      terrain.renderHeight = heightmap.resolution;
      terrain.renderExtentM = heightmap.extent;
    }
    console.log(
      `[Terrain ${preset.id}] ${terrain.name}: ${terrain.worldWidthM.toFixed(0)}×${terrain.worldHeightM.toFixed(0)} m, ` +
        `Δh ${(terrain.heightMaxM - terrain.heightMinM).toFixed(1)} m, ` +
        `WL ${terrain.meta.water_level_m.toFixed(1)} m npm`,
    );
    return terrain;
  } catch (err) {
    console.warn("[Terrain] asset load failed (non-fatal):", err);
    return undefined;
  }
}

function normalizeTerrainUrl(url: string, baseUrl: string): string {
  const root = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  if (url.startsWith(root)) return url;
  return `${root}${url.replace(/^terrain\//, "")}`;
}
